<template>
  <el-container v-loading="loading" :style="themeStyle" class="workspace-shell">
    <el-aside
        :class="['workspace-aside', {'workspace-aside--compact': isSidebarCollapsed}]"
        :width="asideWidth"
    >
      <SideMenu
          :active-index="activeMenuIndex"
          :allow-collapsed-preview="canPreviewCollapsedSidebar"
          :collapsed="isSidebarCollapsed"
          :menu-items="currentMenuItems"
          :platform="currentPlatform"
          @preview-collapse="closeCollapsedSidebarPreview"
          @preview-expand="openCollapsedSidebarPreview"
          @toggle-collapse="toggleSidebar"
      />
    </el-aside>

    <el-container class="workspace-body">
      <div v-if="activeSupportSession" class="support-session-banner" role="status">
        <div class="support-session-banner__content">
          <i class="el-icon-warning-outline"></i>
          <span>
            正在以 <strong>{{ activeSupportSession.targetDisplayName || activeSupportSession.targetUsername }}</strong>
            身份排障，{{ formatSupportExpiresAt(activeSupportSession.expiresAt) }} 到期；系统管理、密码、财务和导出操作已受限并留痕。
          </span>
        </div>
        <el-button plain size="small" type="warning" @click="exitSupportSession">退出代入</el-button>
      </div>
      <el-header class="workspace-header">
        <TopNav
            :breadcrumbs="breadcrumbs"
            :current-user="currentUser"
            :page-description="pageDescription"
            :platform="currentPlatform"
            :platform-options="platformList"
            @logout="handleLogout"
            @open-guide-center="guideCenterVisible = true"
            @switch-platform="switchPlatform"
        />
      </el-header>

      <el-main class="workspace-main">
        <div :class="['workspace-content', {'workspace-content--masked': showComingSoonMask}]">
          <!-- 会话确认前不挂载子页面，避免未登录直达受保护页面时先触发业务接口。 -->
          <router-view v-if="currentUser"/>
          <!-- 待开发入口只遮罩主内容区，保留左侧导航和顶部切换能力。 -->
          <div v-if="currentUser && showComingSoonMask" class="coming-soon-mask" aria-live="polite">
            <div class="coming-soon-panel">
              <i class="el-icon-lock coming-soon-panel__icon"></i>
              <el-tag effect="plain" size="small" type="warning">待开发功能</el-tag>
              <h2>{{ comingSoonTitle }}</h2>
              <p>{{ comingSoonDescription }}</p>
            </div>
          </div>
        </div>
      </el-main>
    </el-container>

    <GuideCenter
        v-if="currentUser"
        v-model="guideCenterVisible"
        :accessible-paths="accessibleGuidePaths"
        :route-name="$route.name || ''"
        @start-guide="handleReplayGuide"
    />
  </el-container>
</template>

<script>
import GuideCenter from '@/components/guide/GuideCenter.vue'
import SideMenu from '@/components/app/SideMenu.vue'
import TopNav from '@/components/app/TopNav.vue'
import {fetchCurrentUser, fetchCurrentUserSilently, logout} from '@/api/modules/auth'
import {stopSupportSession} from '@/api/modules/system'
import {AUTH_EXPIRED_EVENT} from '@/config/devAuth'
import {platformCatalog, platformList, platformMenus} from '@/router/platforms'
import {setTrackingActor, trackEvent} from '@/services/tracker'
import {buildLoginRoute} from '@/utils/authRedirect'
import {setPermissionContext} from '@/utils/permission'
import {initializeTableViewPreferences, resetTableViewPreferences} from '@/services/tableViewPreferences'
import {
  disposeGuideController,
  replayGuide,
  scheduleRouteGuide,
} from '@/guides/controller'
import {initializeGuideProgress, resetGuideProgress} from '@/guides/progress'

function findMenuTrail(menuItems, targetIndex) {
  for (const item of menuItems) {
    if (item.index === targetIndex) {
      return [item.title]
    }
    if (item.children && item.children.length) {
      const childTrail = findMenuTrail(item.children, targetIndex)
      if (childTrail.length) {
        return [item.title].concat(childTrail)
      }
    }
  }
  return []
}

// 动态导航由后端菜单主数据提供。这里保留已发布旧菜单的展示兼容，避免服务尚未重启时侧栏与页面标题不一致。
const ADMIN_MENU_TITLE_OVERRIDES = Object.freeze({
  'admin-resource-master': '合作生态与成员',
  'admin-partners': '合作生态档案',
  'admin-flight-pilots': '合作生态成员',
})

function normalizeAdminNavigation(menuItems = []) {
  return menuItems.map((item) => ({
    ...item,
    title: ADMIN_MENU_TITLE_OVERRIDES[item.code] || item.title,
    children: normalizeAdminNavigation(item.children || []),
  }))
}

export default {
  name: 'MainLayout',
  components: {
    GuideCenter,
    SideMenu,
    TopNav,
  },
  data() {
    return {
      loading: false,
      currentUser: null,
      sidebarCollapsed: false,
      sidebarPreviewExpanded: false,
      compactViewport: false,
      authExpiredHandling: false,
      guideCenterVisible: false,
      supportSessionExpiryTimer: null,
    }
  },
  created() {
    this.loadSession()
  },
  mounted() {
    this.syncSidebarLayout()
    window.addEventListener('resize', this.syncSidebarLayout)
    window.addEventListener(AUTH_EXPIRED_EVENT, this.handleAuthExpired)
  },
  beforeDestroy() {
    disposeGuideController()
    this.clearSupportSessionExpiryTimer()
    window.removeEventListener('resize', this.syncSidebarLayout)
    window.removeEventListener(AUTH_EXPIRED_EVENT, this.handleAuthExpired)
  },
  computed: {
    currentPlatformKey() {
      const matchedRoute = [...this.$route.matched].reverse().find((route) => route.meta && route.meta.platformKey)
      return (matchedRoute && matchedRoute.meta.platformKey) || 'admin'
    },
    currentPlatform() {
      return platformCatalog[this.currentPlatformKey]
    },
    currentRole() {
      if (this.currentUser) {
        return {
          code: this.currentUser.primaryRoleCode || this.currentUser.roleCode,
          name: this.currentUser.primaryRoleName || this.currentUser.roleName,
          roleCodes: this.currentUser.roleCodes || [this.currentUser.roleCode],
          dataScope: this.currentUser.dataScope,
          dataScopeName: this.currentUser.dataScopeName,
          effectiveDataScopeSummary: this.currentUser.effectiveDataScopeSummary,
          menuPermissionCodes: this.currentUser.menuPermissionCodes || [],
        }
      }
      return {
        code: 'SUPER_ADMIN',
        name: '超级管理员',
        menuPermissionCodes: ['*'],
      }
    },
    currentMenuItems() {
      if (this.currentPlatformKey === 'admin' && this.currentUser && Array.isArray(this.currentUser.navigation)) {
        return normalizeAdminNavigation(this.currentUser.navigation)
      }
      return this.filterMenuItems(platformMenus[this.currentPlatformKey] || [])
    },
    activeMenuIndex() {
      // 动态详情页复用列表菜单权限和高亮，避免非超级管理员被误判无菜单权限。
      return this.$route.meta.activeMenu || this.$route.path
    },
    currentMenuTrail() {
      return findMenuTrail(this.currentMenuItems, this.activeMenuIndex)
    },
    currentPrimaryMenu() {
      return this.currentMenuItems.find((item) => {
        return item.index === this.activeMenuIndex || findMenuTrail(item.children || [], this.activeMenuIndex).length
      }) || null
    },
    currentPrimaryHasChildren() {
      return Boolean(this.currentPrimaryMenu && this.currentPrimaryMenu.children && this.currentPrimaryMenu.children.length)
    },
    canPreviewCollapsedSidebar() {
      // 首页等无子菜单页面在桌面端允许悬浮一级菜单临时展开上下文菜单。
      return !this.compactViewport && !this.currentPrimaryHasChildren
    },
    isSidebarCollapsed() {
      // 首页等无子菜单入口不占用空白侧栏；窄屏与用户主动收缩沿用同一紧凑态。
      return this.compactViewport || (!this.sidebarPreviewExpanded && (!this.currentPrimaryHasChildren || this.sidebarCollapsed))
    },
    breadcrumbs() {
      const trail = this.currentMenuTrail.length ? this.currentMenuTrail : [this.$route.meta.title || '工作台']
      return [this.currentPlatform.label].concat(trail)
    },
    pageDescription() {
      // 顶部标题区只保留当前页面的一句话说明，具体文案来自路由元信息。
      return this.$route.meta.description || `${this.$route.meta.title || '当前页面'}功能页面。`
    },
    themeStyle() {
      return {
        '--platform-accent': this.currentPlatform.accent,
      }
    },
    platformList() {
      return platformList
    },
    asideWidth() {
      return this.isSidebarCollapsed ? '72px' : '316px'
    },
    showComingSoonMask() {
      return Boolean(this.$route.meta && this.$route.meta.comingSoon)
    },
    comingSoonTitle() {
      return this.$route.meta.comingSoonTitle || '正在开发中'
    },
    comingSoonDescription() {
      return this.$route.meta.comingSoonDescription || '该功能正在开发中，当前仅开放菜单入口和范围说明。'
    },
    accessibleGuidePaths() {
      const menuPaths = this.flattenMenuItems(this.currentMenuItems)
          .map((item) => item.index)
          .filter((index) => typeof index === 'string' && index.startsWith('/'))
      // 站内消息属于登录后个人工具页，不依赖左侧菜单授权。
      return [...new Set(menuPaths.concat('/admin/notices', '/admin/personal-calendar'))]
    },
    /** 非空时表示当前请求以目标账号权限执行，原管理员身份仅保存在服务端 Session。 */
    activeSupportSession() {
      return this.currentUser && this.currentUser.supportSession ? this.currentUser.supportSession : null
    },
  },
  watch: {
    '$route.fullPath'() {
      this.enforceRoutePermission()
      // 切换一级模块时恢复其合理默认状态：首页收缩，有子菜单的模块展开。
      this.sidebarPreviewExpanded = false
      this.sidebarCollapsed = !this.currentPrimaryHasChildren
      this.guideCenterVisible = false
      this.$nextTick(() => this.scheduleCurrentRouteGuide())
    },
    'currentUser.supportSession'() {
      this.scheduleSupportSessionExpiryCheck()
    },
  },
  methods: {
    async loadSession() {
      this.loading = true
      try {
        const currentUser = await fetchCurrentUser()
        if (currentUser.passwordChangeRequired) {
          await this.$router.replace('/change-password')
          return
        }
        setPermissionContext(currentUser)
        setTrackingActor(currentUser)
        try {
          await initializeTableViewPreferences()
        } catch (error) {
          // 列表仍以精简字段可用，保存时会向用户明确提示失败原因。
          resetTableViewPreferences()
        }
        this.currentUser = currentUser
        this.scheduleSupportSessionExpiryCheck()
        this.enforceRoutePermission()
        // 使用指引进度独立加载，不阻塞后台页面；失败时只取消自动弹出。
        initializeGuideProgress().then((ready) => {
          if (ready && this.currentUser) this.$nextTick(() => this.scheduleCurrentRouteGuide())
        })
      } catch (error) {
        resetTableViewPreferences()
        resetGuideProgress()
        setTrackingActor(null)
        this.redirectToLogin()
      } finally {
        this.loading = false
      }
    },
    filterMenuItems(menuItems) {
      if ((this.currentRole.roleCodes || [this.currentRole.code]).includes('SUPER_ADMIN')) {
        return menuItems
      }
      const allowed = new Set(this.currentRole.menuPermissionCodes || [])
      return menuItems
          .map((item) => {
            if (item.children && item.children.length) {
              const children = this.filterMenuItems(item.children)
              return children.length ? {...item, children} : null
            }
            return allowed.has('*') || allowed.has(item.permissionCode) ? item : null
          })
          .filter(Boolean)
    },
    flattenMenuItems(menuItems) {
      return menuItems.reduce((result, item) => {
        if (item.children && item.children.length) {
          return result.concat(this.flattenMenuItems(item.children))
        }
        return result.concat(item)
      }, [])
    },
    enforceRoutePermission() {
      if (!this.currentUser) {
        return
      }
      // 顶部站内消息等个人工具页只依赖登录态，不要求出现在左侧业务菜单中。
      if (this.$route.meta && this.$route.meta.utilityRoute) {
        return
      }
      const currentItems = this.flattenMenuItems(this.currentMenuItems)
      if (!currentItems.length) {
        this.redirectToForbidden()
        return
      }
      if (!currentItems.length || currentItems.some((item) => item.index === this.activeMenuIndex)) {
        return
      }
      this.redirectToForbidden()
    },
    redirectToForbidden() {
      if (this.$route.path === '/admin/forbidden') return
      this.$router.replace({path: '/admin/forbidden', query: {from: this.$route.fullPath}}).catch(() => {})
    },
    switchPlatform(platformKey) {
      const target = platformCatalog[platformKey]
      if (target) {
        this.$router.push(target.home)
      }
    },
    async handleLogout() {
      const loginRoute = buildLoginRoute(this.$route.fullPath)
      trackEvent('AUTH', '后台退出登录', {resultStatus: 'SUCCESS'})
      await logout()
      disposeGuideController()
      resetTableViewPreferences()
      resetGuideProgress()
      setPermissionContext(null)
      setTrackingActor(null)
      this.$router.replace(loginRoute)
    },
    /** 主动退出时由服务端恢复真实管理员，再重新加载菜单、权限与数据范围。 */
    async exitSupportSession() {
      try {
        await this.$confirm('退出后将恢复原超级管理员身份，是否继续？', '退出代入排障', {
          type: 'warning',
          confirmButtonText: '退出代入',
          cancelButtonText: '取消',
        })
        await stopSupportSession()
        await this.$router.replace('/admin/dashboard')
        await this.loadSession()
        this.$message.success('已退出代入排障，当前为原管理员身份。')
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(error.message || '退出代入排障失败')
        }
      }
    },
    formatSupportExpiresAt(value) {
      return value ? String(value).replace('T', ' ') : '--'
    },
    clearSupportSessionExpiryTimer() {
      if (this.supportSessionExpiryTimer) {
        window.clearTimeout(this.supportSessionExpiryTimer)
        this.supportSessionExpiryTimer = null
      }
    },
    /** 本地提示到期后主动读取登录态，服务端会先校验数据库再安全恢复真实管理员。 */
    scheduleSupportSessionExpiryCheck() {
      this.clearSupportSessionExpiryTimer()
      if (!this.activeSupportSession || !this.activeSupportSession.expiresAt) return
      const expiresAt = new Date(this.activeSupportSession.expiresAt).getTime()
      const delay = Number.isFinite(expiresAt) ? Math.max(0, expiresAt - Date.now()) : 0
      this.supportSessionExpiryTimer = window.setTimeout(() => this.refreshExpiredSupportSession(), delay + 300)
    },
    async refreshExpiredSupportSession() {
      const wasSupporting = Boolean(this.activeSupportSession)
      await this.loadSession()
      if (wasSupporting && !this.activeSupportSession) {
        await this.$router.replace('/admin/dashboard')
        this.$message.warning('排障会话已到期，已恢复原管理员身份。')
      }
    },
    async handleAuthExpired(event) {
      if (this.authExpiredHandling) {
        return
      }
      this.authExpiredHandling = true
      try {
        // 登录成功后的旧 401 事件可能晚到，先确认 Session，避免把有效用户又带回登录页。
        this.currentUser = await fetchCurrentUserSilently()
        this.scheduleSupportSessionExpiryCheck()
        if (this.currentUser.passwordChangeRequired) {
          await this.$router.replace('/change-password')
          return
        }
        setPermissionContext(this.currentUser)
        setTrackingActor(this.currentUser)
        this.enforceRoutePermission()
        initializeGuideProgress().then((ready) => {
          if (ready && this.currentUser) this.$nextTick(() => this.scheduleCurrentRouteGuide())
        })
        this.authExpiredHandling = false
        return
      } catch (error) {
      }
      this.currentUser = null
      setPermissionContext(null)
      resetTableViewPreferences()
      resetGuideProgress()
      disposeGuideController()
      setTrackingActor(null)
      trackEvent('AUTH', '后台登录失效', {resultStatus: 'FAILED'})
      this.$message.warning(event.detail?.message || '登录状态已失效，请重新登录。')
      // 统一收口 401 场景，避免子页面继续停留在半登录状态。
      this.redirectToLogin()
    },
    redirectToLogin() {
      this.$router.replace(buildLoginRoute(this.$route.fullPath)).catch(() => {
      })
    },
    scheduleCurrentRouteGuide() {
      if (!this.currentUser || this.showComingSoonMask || this.$route.meta?.utilityRoute && this.$route.name === 'admin-forbidden') return
      scheduleRouteGuide(this.$route, {
        getCurrentFullPath: () => this.$route.fullPath,
      })
    },
    handleReplayGuide(guide) {
      if (!replayGuide(guide)) this.$message.warning('当前页面尚未渲染完成，请稍后重试。')
    },
    syncSidebarLayout() {
      // 中等宽度仍保留主内容区域，优先收缩菜单而不是改成上下堆叠。
      this.compactViewport = window.innerWidth <= 1024
      if (this.compactViewport || !this.currentPrimaryHasChildren) {
        this.sidebarPreviewExpanded = false
      }
      if (!this.currentPrimaryHasChildren) {
        this.sidebarCollapsed = true
      }
    },
    openCollapsedSidebarPreview() {
      if (this.canPreviewCollapsedSidebar) {
        this.sidebarPreviewExpanded = true
      }
    },
    closeCollapsedSidebarPreview() {
      this.sidebarPreviewExpanded = false
    },
    toggleSidebar() {
      // 无子菜单时保持紧凑态，避免展开没有内容的上下文栏。
      if (this.compactViewport || !this.currentPrimaryHasChildren) {
        return
      }
      this.sidebarCollapsed = !this.sidebarCollapsed
    },
  },
}
</script>

<style scoped>
.support-session-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 52px;
  padding: 10px 24px;
  border-bottom: 1px solid #f0b95c;
  background: #fff5df;
  color: #7a4b00;
}

.support-session-banner__content {
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.55;
}

.support-session-banner__content > i {
  font-size: 20px;
}

.workspace-content--masked {
  position: relative;
  min-height: calc(100vh - 124px);
}

.coming-soon-mask {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  padding: 24px;
  border-radius: var(--radius-lg);
  background: rgba(243, 246, 251, 0.82);
  backdrop-filter: blur(2px);
}

.coming-soon-panel {
  width: min(420px, 100%);
  padding: 28px 32px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.96);
  box-shadow: var(--shadow-soft);
  text-align: center;
}

.coming-soon-panel__icon {
  display: block;
  margin-bottom: 12px;
  color: var(--platform-accent);
  font-size: 34px;
}

.coming-soon-panel h2 {
  margin: 12px 0 8px;
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 0;
}

.coming-soon-panel p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.7;
}

@media (max-width: 720px) {
  .support-session-banner {
    align-items: flex-start;
    padding: 10px 16px;
  }

  .support-session-banner__content {
    align-items: flex-start;
  }

  .coming-soon-mask {
    align-items: flex-start;
    padding: 18px;
  }

  .coming-soon-panel {
    padding: 24px 20px;
  }
}
</style>
